import { reactive } from 'vue';
import { COUNTRY_CODE } from '@/utils/intl.js';
import { unpack } from '@/utils/node-preview.js';
import { format } from '@/utils/time.js';

// The cache is reactive so 'categoryTree' recomputes in every subscribed component when the tree
// arrives; PENDING is deliberately NOT reactive so a failed load (which leaves the cache empty)
// doesn't invalidate active computeds and retry in a loop - instead the retry happens the next time
// 'categoryTree' is evaluated fresh (e.g. by a newly mounted component)
const CACHE = reactive({ date: null, country: null, tree: null });
let PENDING = false;

function getSeasonalDescendants(node) {
  const best = {};
  const walk = (x, prefix) => {
    for (const c of x.children) {
      c.path = c.fragment.startsWith('/') ? null : [ ...prefix, c.fragment ];
      if (c.seasonalScore > 0 && (!best[c.title] || c.path.length < best[c.title].path.length))
        best[c.title] = c;
      if (c.path)
        walk(c, c.path);
    }
  };
  walk(node, []);
  return _.orderBy(_.values(best), 'seasonalScore', 'desc');
}

function withFeatured(props) {
  return {
    ...props,
    get featuredStatic() {
      return this.children.filter(x => x.isFeatured);
    },
    get featuredSeasonal() {
      return getSeasonalDescendants(this);
    },
    get featured() {
      return [ ...this.featuredSeasonal, ...this.featuredStatic.filter(x => x.seasonalScore <= 0) ];
    },
    getPath(base) {
      if (this.fragment.startsWith('/'))
        return this.fragment.split('/').slice(1);
      if (this.path)
        return [ ...base, ...this.path ];
      return [ ...base, this.fragment ];
    }
  };
}

export default {
  data() {
    return {
      loadCategoryTreeStatus: this.$track('loadCategoryTree')
    };
  },

  computed: {
    categoryTree() {
      // Always returns the category tree for the current date / country, kicking off a (re)load as
      // needed - null while loading or if the load failed
      this.loadCategoryTree(format(new Date(), 'iso-date'), COUNTRY_CODE).catch(() => {});
      return CACHE.tree;
    }
  },

  methods: {
    async loadCategoryTree(date, country) {
      if ((CACHE.date == date) && (CACHE.country == country) && (CACHE.tree || PENDING))
        return;

      PENDING = true;
      CACHE.date = date;
      CACHE.country = country;
      CACHE.tree = null;

      try {
        const text = await this.$call('get-category-tree', { date, country });
        if ((CACHE.date != date) || (CACHE.country != country))
          // This load was superseded while in flight
          return;

        const stack = [ withFeatured({ children: [] }) ];
        for (const line of text.split('\n')) {
          if (!line.trim())
            continue;
          const depth = line.length - line.trimStart().length;
          const [ , token, title, keys ] = line.trim().match(/^(\S+) "([^"]*)" ?(.*)$/);
          const [ fragment, ...flags ] = token.split('#');
          const dates = flags.map(x => x.match(/^(\d{4}-\d\d-\d\d)(?::(\d{4}-\d\d-\d\d))?$/)).find(x => x);
          const score = flags.find(x => /^S[-.\d]/.test(x));
          stack[depth].children.push(
            stack[depth + 1] = withFeatured({
              fragment,
              title,
              isFeatured: flags.includes('F'),
              seasonalScore: score ? +score.slice(1) : 0,
              seasonal: dates ? { start: dates[1], end: dates[2] } : null,
              children: [],
              preview: keys.split(' ').filter(x => x).map(unpack)
            })
          );
        }
        CACHE.tree = stack[0];

        const greetingCards = CACHE.tree.children.find(x => x.fragment == 'greeting-cards');
        CACHE.tree.children.push({
          ...greetingCards,
          fragment: 'group-signed-greeting-cards',
          title: 'group-signed greeting cards',
          isFeatured: false
        });
      } finally {
        if ((CACHE.date == date) && (CACHE.country == country))
          PENDING = false;
      }
    }
  }
};
